import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { Checkbox, IconButton, Link, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { useMemo, useState } from 'react'
import type { CatalogRow } from './CatalogList.types'
import './CatalogList.css'

export interface CatalogTableProps {
  rows: CatalogRow[]
  onRowClick: (row: CatalogRow) => void
}

const formatFee = (fee: number) => (fee ? `${Math.round(Number(fee) * 10000) / 100}%` : '')

export function CatalogTable({ rows, onRowClick }: CatalogTableProps) {
  const childrenByParentId = useMemo(() => {
    const map = new Map<number, CatalogRow[]>()
    rows.forEach(r => {
      if (r.parentId !== null) {
        const arr = map.get(r.parentId) ?? []
        arr.push(r)
        map.set(r.parentId, arr)
      }
    })
    return map
  }, [rows])

  const parentRows = useMemo(() => rows.filter(r => r.parentId === null), [rows])

  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set(parentRows.map(r => r.id)))

  const toggleExpand = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  return (
    <Table stickyHeader className="catalog-table">
      <TableHead>
        <TableRow>
          <TableCell width="12%">Contract Vehicle</TableCell>
          <TableCell width="11%">Contract #</TableCell>
          <TableCell width="15%">Contract Name</TableCell>
          <TableCell width="8%">Contract Type</TableCell>
          <TableCell width="9%">Prime/Sub/JV</TableCell>
          <TableCell width="9%">Set Aside</TableCell>
          <TableCell width="14%">Scope</TableCell>
          <TableCell width="9%">Contract Fee %</TableCell>
          <TableCell width="9%">Partner Fee %</TableCell>
          <TableCell width="4%">Active</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {parentRows.map(parent => {
          const children = childrenByParentId.get(parent.id) ?? []
          const hasChildren = children.length > 0
          const isExpanded = expandedIds.has(parent.id)

          return [
            <TableRow key={parent.id} hover>
              <TableCell>
                <div className="catalog-vehicle-cell">
                  {hasChildren ? (
                    <IconButton size="small" onClick={() => toggleExpand(parent.id)} className="catalog-expand-btn">
                      {isExpanded ? (
                        <KeyboardArrowUpIcon fontSize="small" />
                      ) : (
                        <KeyboardArrowDownIcon fontSize="small" />
                      )}
                    </IconButton>
                  ) : (
                    <div className="catalog-expand-spacer" />
                  )}
                  {parent.contractVehicle}
                </div>
              </TableCell>

              <TableCell>
                <Link component="button" underline="hover" onClick={() => onRowClick(parent)}>
                  {parent.contractNumber}
                </Link>
              </TableCell>

              <TableCell>
                <Link component="button" underline="hover" onClick={() => onRowClick(parent)}>
                  {parent.contractName}
                </Link>
              </TableCell>

              <TableCell>{parent.contractType}</TableCell>
              <TableCell>{parent.primeSubJV}</TableCell>
              <TableCell>{parent.setAside}</TableCell>
              <TableCell>{parent.scope}</TableCell>
              <TableCell>{formatFee(parent.contractFee)}</TableCell>
              <TableCell>{formatFee(parent.partnerFee)}</TableCell>

              <TableCell>
                <Checkbox checked={parent.status === 'Active'} disabled displayOnly />
              </TableCell>
            </TableRow>,

            ...(isExpanded
              ? children.map(child => (
                  <TableRow key={child.id} hover className="catalog-child-row">
                    <TableCell className="catalog-child-first-cell">{child.contractVehicle}</TableCell>

                    <TableCell>
                      <Link component="button" underline="hover" onClick={() => onRowClick(child)}>
                        {child.contractNumber}
                      </Link>
                    </TableCell>

                    <TableCell>
                      <Link component="button" underline="hover" onClick={() => onRowClick(child)}>
                        {child.contractName}
                      </Link>
                    </TableCell>

                    <TableCell>{child.contractType}</TableCell>
                    <TableCell>{child.primeSubJV}</TableCell>
                    <TableCell>{child.setAside}</TableCell>
                    <TableCell>{child.scope}</TableCell>
                    <TableCell>{formatFee(child.contractFee)}</TableCell>
                    <TableCell>{formatFee(child.partnerFee)}</TableCell>

                    <TableCell>
                      <Checkbox checked={child.status === 'Active'} disabled displayOnly />
                    </TableCell>
                  </TableRow>
                ))
              : []),
          ]
        })}
      </TableBody>
    </Table>
  )
}
